import React, { useEffect, useRef, useState, memo, useMemo, useCallback, useLayoutEffect } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { handleSecondaryViewOpen, handleSecondaryViewWidth, handleSidebarOpen, handleAddLastTab } from 'store/appSlice';
import { EditwriteIcon } from 'components/icons/EditwriteIcon';
import { ChevronDownIcon } from 'components/icons/ChevronDownIcon';
import { SearchIcon } from 'components/icons/SearchIcon';
import { CloseIcon } from 'components/icons/CloseIcon';
import { useWindowSize } from 'hooks';
import { RootState } from 'store';
import 'react-perfect-scrollbar/dist/css/styles.css';
import api from 'services/api';
import moment from 'moment';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import { TabScrollButton, TabScrollButtonProps } from '@mui/material';

import TranscriptView2 from './TranscriptViewV2';
import ChatView from './ChatView';
import NoteView from './NoteView';
import { isMobile } from 'react-device-detect';
import OverView from './OverView';
import TimelineView from './TimelineVIew';
import ChapterView from './ChapterView';

import { decryptAES_CBC } from 'hooks/useImageDecrypt';
import { addVideoShot } from 'store/shotsSlice';
import SearchView from './SearchView';

import VideoInfoSkeleton from './_components/VideoInfoSkeleton';
import VideoStatistics from './_components/Statistics';
import PeopleList from './_components/PeopleList';
import VideoShotDropdown from './_components/VideoShotDropdown';
import { getVideoByHashId } from 'store/decryptImagesSlice';

// import { AnimatePresence, motion } from 'framer-motion';

import {
  getLastVideoByChannel,
  removeChannel,
  setSelectedTab,
  toggleActivePlayer
} from 'store/secondaryChannelVideoSlice';
import dayjs from 'dayjs';

const AntTabs = styled(Tabs)({
  minHeight: 36,
  alignItems: 'center',
  position: 'relative',
  '.right-scroll-button, .left-scroll-button': {
    display: 'flex !important'
  },
  '.right-scroll-button::before': {
    height: 32,
    width: 32,
    content: '""',
    pointerEvents: 'none',
    position: 'absolute',
    right: 40,
    zIndex: 100,
    background: 'linear-gradient(to left,rgb(255,255,255) 10%, rgba(249,249,249,0) 90%)'
  },
  '.left-scroll-button::after': {
    height: 32,
    width: 32,
    content: '""',
    pointerEvents: 'none',
    position: 'absolute',
    left: 40,
    zIndex: 100,
    background: 'linear-gradient(to right,rgb(255,255,255) 10%, rgba(249,249,249,0) 90%)'
  },
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .MuiTabs-scrollButtons': {
    borderRadius: '100%',
    height: '100%',
    zIndex: 3,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)'
    }
  },
  '& .MuiTabs-scroller': {
    zIndex: 2
  }
});

const AntTab = styled((props: any) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  padding: '0px 12px !important',
  minHeight: 32,
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0
  },
  fontWeight: 400,
  fontSize: 12.5,
  marginRight: theme.spacing(1),
  color: '#111',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  borderRadius: 8,
  fontFamily: 'Lato, sans-serif',
  '&:hover': {
    color: '#111',
    opacity: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)'
  },
  '&.Mui-selected': {
    color: '#ffffff',
    backgroundColor: '#111'
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff'
  }
}));

function SecondaryView() {
  const dispatch = useDispatch();
  // const { allActivities, activities } = useSelector((state: RootState) => state.activity);
  const secondaryWidth = useSelector((state: RootState) => state.app.secondaryViewWidth);
  const [selectedVideoName, setSelectedVideoName] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null as any);
  const location = useLocation();

  const channelIdRegex = /\/channel\/([^/]+)/;
  const channelMatch = location.pathname.match(channelIdRegex);
  const channelId = channelMatch ? channelMatch[1] : 'home';
  const videoMatch = location.pathname.match(/\/video\/([^/]+)/);
  const videoId = videoMatch ? videoMatch[1] : undefined;
  const activities = useSelector((state: RootState) => state.userChannelActivity);
  const videos = useSelector((state: RootState) => state.userChannelVideo);

  const [isResizing, setIsResizing] = useState(false);
  const [manualResize, setManualResize] = useState(false);
  const [displayResizer] = useState(true);
  const [isEditable, handleIsEditible] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const [width] = useWindowSize();
  const [keywords, setkeyword] = useState(null as any);
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  const [selectedChannel, setSelectedChannel] = useState(null as any);
  const [showHighlight, handleShowHighlight] = useState<boolean>(true);
  // const titleRef = useRef(document.createElement('div'));
  const { secondaryViewOpen } = useSelector((state: RootState) => state.app);
  const inputRef = useRef(null as any);
  const { workspaceId } = useParams();
  const secondaryViewData = useSelector((state: RootState) => state.app.secondaryViewData);
  const secondaryViewSlice = useSelector((state: RootState) => state.secondaryChannelVideos); // Access the secondaryView state using useSelector
  const [currentVideoShot, setCurrentVideoShot] = useState<number>(0);
  // const [hasSelectedShot, setHasSelectedShot] = useState(false);
  const [selected, setSelected] = useState<number>(0); // selected tab. e.g oveview, summary, etc
  const playerIsActive = sessionStorage.getItem('playerActive');
  const playlistVideos = useSelector((state: RootState) => state.channelPlaylists.channels);
  const videoShotSlice = useSelector((state: RootState) => state.videoShots.videos);
  const [videoShotsData, setVideoShotData] = useState(null as any);
  // const secondaryViewVideos = useSelector((state: RootState) => state.secondaryView);
  // const video = getSecondaryOpenVideo(secondaryViewVideos, channelId);
  const secondaryViewVideos = useSelector((state: RootState) => state.secondaryChannelVideos);
  const video = getLastVideoByChannel(secondaryViewVideos, channelId);
  const encryptedThumbnailsState = useSelector((state: RootState) => state.decryptedImages);

  const [loading, setLoading] = useState<boolean>(true);
  const [encryptionData, setEncryptionData] = useState(null as any);
  const encrytedInfo = getVideoByHashId(encryptedThumbnailsState, secondaryViewData.hashId);

  useEffect(() => {
    setEncryptionData(encrytedInfo?.decryptionKeys);
    return () => {
      setEncryptionData(null);
    };
  }, [channelId, secondaryViewData.hashId, encrytedInfo]);

  useEffect(() => {
    const fetchVideoShots = async () => {
      const response = await api.get(`/api/videos/${secondaryViewData.hashId}/shots/`);
      dispatch(addVideoShot({ videoHashId: secondaryViewData.hashId, videoData: response.data }));
      setVideoShotData(response.data);
    };

    if (secondaryViewData.hashId) {
      if (videoShotSlice[secondaryViewData.hashId]?.length) {
        setVideoShotData(videoShotSlice[secondaryViewData.hashId]);
      } else {
        fetchVideoShots();
      }
    }
    return () => {
      setVideoShotData([]);
    };
  }, [secondaryViewData.hashId]);

  useEffect(() => {
    if (video && video.selectedShot !== undefined) {
      setCurrentVideoShot(video.selectedShot);
    }
    if (video && video.selectedTab) {
      setSelected(video.selectedTab);
    }

    return () => {
      setSelected(0);
      setCurrentVideoShot(0);
    };
  }, [channelId, secondaryViewData.hashId, video?.selectedShot]);

  const handleNextButtonClick = () => {
    const tabsContainer = document.querySelector('.MuiTabs-scroller');
    if (tabsContainer) {
      const tabsContainer = document.querySelector('.MuiTabs-scroller') as HTMLElement;
      const scrollRightButtonWrap = document.querySelector('.right-scroll-button-wrap') as HTMLElement;
      if (tabsContainer.scrollLeft + 2 * tabsContainer.clientWidth > tabsContainer.scrollWidth) {
        scrollRightButtonWrap.classList.add('hidden');
      }
      tabsContainer.scrollTo({
        left: tabsContainer.scrollLeft + tabsContainer.clientWidth,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (inputRef.current && !inputRef?.current?.contains(event.target as Node)) {
        inputRef.current.blur();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputRef]);

  const handlePrevButtonClick = () => {
    const tabsContainer = document.querySelector('.MuiTabs-scroller');
    if (tabsContainer) {
      const scrollLeftButtonWrap = document.querySelector('.left-scroll-button-wrap') as HTMLElement;
      if (tabsContainer.clientWidth > tabsContainer.scrollLeft) {
        scrollLeftButtonWrap.classList.add('hidden');
      }
      tabsContainer.scrollTo({
        left: tabsContainer.scrollLeft - tabsContainer.clientWidth,
        behavior: 'smooth'
      });
    }
  };

  const tabScrollButtonRef = (props: TabScrollButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const { direction, disabled } = props;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const buttonRef = useRef<HTMLButtonElement>(null);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (ref && buttonRef.current) {
        // Set the ref of the TabScrollButton to the button element
        // and assign a data-testid attribute for testing purposes
        buttonRef.current.setAttribute('data-testid', `tab-scroll-button-${direction}`);
      }
    }, [direction, ref]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const tabScrollButton = document.querySelector('.MuiTabs-scroller') as HTMLElement;
      tabScrollButton?.addEventListener('wheel', handleMouseWheelScroll);

      return () => {
        tabScrollButton?.removeEventListener('wheel', handleMouseWheelScroll);
      };
    }, [channelId]);

    function handleMouseWheelScroll(event: WheelEvent) {
      const tabList = document.querySelector('.MuiTabs-scroller') as HTMLElement;
      const currentScrollLeft = tabList.scrollLeft;
      const maxScrollLeft = tabList.scrollWidth - tabList.clientWidth;
      const delta = Math.sign(event.deltaY);
      const newScrollLeft = currentScrollLeft + delta * 120;
      if (newScrollLeft <= 0) {
        const scrollLeftButtonWrap = document.querySelector('.left-scroll-button-wrap') as HTMLElement;
        scrollLeftButtonWrap.classList.add('hidden');
        tabList.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (newScrollLeft >= maxScrollLeft) {
        const scrollRightButtonWrap = document.querySelector('.right-scroll-button-wrap') as HTMLElement;
        scrollRightButtonWrap.classList.add('hidden');
        tabList.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
      } else {
        tabList.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      }
    }

    return (
      <div
        className={`w-10 h-full bg-white z-30 absolute custom-scroll-button-wrap ${
          direction === 'left'
            ? `left-0 left-scroll-button-wrap ${disabled ? 'hidden' : 'block'}`
            : `right-0 right-scroll-button-wrap ${disabled ? 'hidden' : 'block'}`
        }`}
      >
        <TabScrollButton
          {...props}
          ref={buttonRef}
          style={{
            display: direction === 'left' ? 'inherit' : 'inherit'
          }}
          className={
            direction === 'left'
              ? 'MuiTabs-scrollButtons left-scroll-button'
              : 'MuiTabs-scrollButtons right-scroll-button'
          }
          onClick={direction === 'left' ? handlePrevButtonClick : handleNextButtonClick}
        />
      </div>
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelected(newValue);
    dispatch(setSelectedTab({ channelHashId: channelId, videoHashId: secondaryViewData.hashId, tab: newValue }));
  };

  const navigateTo = useNavigate();

  useEffect(() => {
    if (secondaryViewData) {
      setSelectedChannel(secondaryViewData.selectedChannel);
    }

    return () => {
      setSelectedChannel(null);
    };
  }, [channelId, secondaryViewData.hashId]);

  const channelHashId = secondaryViewData.selectedChannel?.hash_id || 'home';
  const currentVideo = useMemo(() => {
    return (
      // need to refactor for videos
      secondaryViewVideos.channels[channelHashId]?.lastVideoData
      // videos[channelHashId]?.results.find((video: any) => video.hash_id === secondaryViewData?.hashId) ||
      // activities[channelHashId]?.results.find(
      //   (item: any) => item?.content_object?.hash_id === secondaryViewData?.hashId
      // )?.content_object ||
      // playlistVideos[channelHashId]?.find((video: any) => video.hash_id === secondaryViewData?.hashId)
    );
  }, [
    channelHashId,
    secondaryViewData?.hashId,
    // videos[channelHashId],
    // activities[channelHashId],
    // playlistVideos[channelHashId]
    secondaryViewVideos.channels[channelHashId]?.lastVideoData
  ]);

  useEffect(() => {
    if (currentVideo) {
      setSelectedVideo(currentVideo);
      setSelectedChannel(secondaryViewData.selectedChannel);
      setSelectedVideoName(currentVideo?.generated_title ? currentVideo?.generated_title : currentVideo.name);
      setLoading(false);
    }
  }, [secondaryViewData.hashId, currentVideo]);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const response = await api.get(`api/channels/${channelId}/videos/${secondaryViewData.hashId}/`);
        const video = response.data;
        setSelectedVideo(video);
        setLoading(false);
        if (video?.keyframe_aes_key_id && video?.enc_data) {
          const { data, iv } = video.enc_data;
          await decryptAES_CBC(data, iv, secondaryViewData.hashId);
        }
      } catch (error) {
        console.error('Error fetching video:', error);
      }
    };

    if (!currentVideo && secondaryViewData.hashId) {
      fetchVideo();
    }
  }, [secondaryViewData?.hashId, selectedChannel?.hash_id, currentVideo]);

  const startResizing = React.useCallback(() => {
    setManualResize(true);
    setIsResizing(true);
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback(
    (mouseMoveEvent) => {
      if (isResizing) {
        if (manualResize) {
          if (mouseMoveEvent.clientX > 450) {
            if (window.innerWidth - mouseMoveEvent.clientX > 1000) {
              dispatch(handleSecondaryViewWidth(1000));
            } else if (window.innerWidth - mouseMoveEvent.clientX < 300) {
              dispatch(handleSecondaryViewWidth(300));
            } else {
              dispatch(handleSecondaryViewWidth(window.innerWidth - mouseMoveEvent.clientX));
            }
          }
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (secondaryWidth + 450 > window.innerWidth) {
      if (window.innerWidth - 450 > 200) {
        dispatch(handleSecondaryViewWidth(window.innerWidth - 450));
      } else {
        dispatch(handleSecondaryViewWidth(200));
      }
    }
  }, [width]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const handleDblClick = (e: any) => {
    if (e.detail === 2) {
      setManualResize(false);
      dispatch(handleSecondaryViewWidth(450));
    }
  };

  const updateVideoTitle = async () => {
    await api.put(`api/videos/${secondaryViewData.hashId}/update/`, {
      generated_title: selectedVideoName,
      name: selectedVideoName
    });
    handleIsEditible(false);
  };

  const navigateChannel = (channelHashId?: any) => {
    setExpanded(false);
    handleIsEditible(false);
    dispatch(handleSidebarOpen(true));

    // const videoElement = document.getElementById((channelHashId ? channelHashId : 'home') + '-' + secondaryViewData.id);
    // if (videoElement) {
    //   videoElement.scrollIntoView();
    //   videoElement.classList.add('animate-video-background-highlight');
    //   setTimeout(() => {
    //     videoElement.classList.remove('animate-video-background-highlight');
    //   }, 3000);
    // } else {
    //   // dispatch(
    //   //   setRecentVideoPrevNextGroup({
    //   //     key: channelHashId ? channelHashId : 'home'
    //   //   })
    //   // );
    // }

    navigateTo(
      channelHashId
        ? `/workspace/${workspaceId}/user/channel/${channelHashId}`
        : `/workspace/${workspaceId}/videos/all`,
      {
        state: {
          fromNotification: true,
          videoElement: (channelHashId ? channelHashId : 'home') + '-' + secondaryViewData.id,
          dateAdded: secondaryViewData.dateAdded,
          channelHashId: channelHashId ? channelHashId : 'home'
        }
      }
    );
  };

  const navigateVideoPage = useCallback(
    (seekTime = null) => {
      if (!videoId || (videoId && videoId !== secondaryViewData.hashId)) {
        navigateTo(
          `/${
            secondaryViewData.channelId
              ? `workspace/${workspaceId}/user/channel/${secondaryViewData.channelId}`
              : 'home'
          }/video/${secondaryViewData.hashId}`,
          {
            state: {
              video: selectedVideo,
              seekTime: seekTime,
              channel: selectedChannel
            }
          }
        );
      }
    },
    [videoId, channelId, secondaryViewData.hashId, selectedChannel, selectedVideo]
  );

  const jumpToVideo = (videoId: string) => {
    setExpanded(false);
    const videoElement = document.getElementById(videoId);
    if (videoElement) {
      videoElement.scrollIntoView();
      const closeMenu = document.getElementById('menu-close');
      closeMenu?.click();
    }
  };

  const navigatePlaylist = (playlist: any) => {
    const isActivePlayer = sessionStorage.getItem('playerActive');
    if (isActivePlayer) {
      dispatch(toggleActivePlayer({ channelHashId: channelId, activePlayer: false }));
    }

    const videoElement = document.getElementById(
      selectedChannel.hash_id + '-' + secondaryViewData.id + '-' + playlist.hash_id
    );
    if (videoElement) {
      videoElement.scrollIntoView();
      videoElement.classList.add('animate-video-background-highlight');
      setTimeout(() => {
        videoElement.classList.remove('animate-video-background-highlight');
      }, 3000);
    } else {
      navigateTo(`/workspace/${workspaceId}/channel/${selectedChannel.hash_id}/playlist/${playlist.hash_id}`, {
        state: {
          videoElement: selectedChannel.hash_id + '-' + secondaryViewData.id + '-' + playlist.hash_id
        }
      });
    }

    if (isMobile) {
      dispatch(handleSecondaryViewOpen({ open: false }));
    }
  };

  useEffect(() => {
    if (secondaryViewData?.hashId) {
      api.get(`/api/videos/${secondaryViewData.hashId}/keywords/`).then((res) => setkeyword(res.data));
    }
  }, [secondaryViewData?.hashId, channelId]);

  const mobileStyles =
    isMobile && videoId
      ? {
          bottom: '0',
          width: '100%',
          height: '50vh'
        }
      : {
          height: `${window.innerHeight - 44}px`
        };

  return (
    <>
      {secondaryViewOpen && !loading && (
        <div
          key={secondaryViewData.hashId}
          className={clsx('font-lato flex-1', {
            'absolute right-0 w-full z-10 bg-white': isMobile,
            static: !isMobile
          })}
          style={mobileStyles}
        >
          <div
            className={clsx('font-lato', 'flex-1', {
              'absolute right-0 w-full z-10 bg-white': isMobile,
              static: !isMobile
            })}
            style={mobileStyles}
          >
            <div
              id="secondary"
              style={{
                width: isMobile ? '100%' : secondaryWidth
              }}
              className={`app-secondary flex flex-col md:right-auto md:top-auto h-full`}
            >
              {isMobile ? (
                <></>
              ) : (
                <div
                  className={clsx('app-secondary-resizer', {
                    block: displayResizer,
                    hidden: !displayResizer
                  })}
                  style={{ right: secondaryWidth - 4 }}
                  onMouseDown={startResizing}
                  onClick={handleDblClick}
                />
              )}
              <div
                className={clsx('app-secondary-content flex flex-1 flex-col', {
                  'border-l border-line': !videoId,
                  shadow: videoId,
                  'z-50': videoId
                })}
              >
                <div className="absolute top-0 right-2 z-50">
                  {playerIsActive !== 'true' && (
                    <div
                      className="w-9 h-6 flex items-center justify-center cursor-pointer text-gray-800 text-opacity-70 hover:text-opacity-100"
                      onClick={() => {
                        dispatch(handleSecondaryViewOpen({ open: false }));
                        dispatch(removeChannel({ channelId }));
                      }}
                    >
                      <CloseIcon className="w-5 h-6" />
                    </div>
                  )}
                </div>
                <div className="hover:bg-[#f2f2f2] hover:bg-opacity-70 relative">
                  {selectedVideo && !videoId && (
                    <>
                      <div className="w-full pt-5 pb-2">
                        <div className="px-4">
                          {isEditable ? (
                            <div className="">
                              <input
                                type="text"
                                className="w-full px-2 border border-[#999999]"
                                name="title"
                                value={selectedVideoName}
                                onChange={(e) => {
                                  setSelectedVideoName(e.target.value);
                                }}
                              />
                              <div className="flex mt-1 text-[13px]">
                                <button
                                  className="bg-[#1ab394] py-[3px] px-[10px] text-white"
                                  onClick={updateVideoTitle}
                                >
                                  Update
                                </button>
                                <button
                                  className="ml-2 py-[3px] px-[10px] text-black border border-[#e7eaec]"
                                  onClick={() => handleIsEditible(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="group flex w-full">
                                <span
                                  className={`font-semibold ${
                                    !videoId || (videoId && videoId !== secondaryViewData.hashId)
                                      ? 'hover:text-blue-text cursor-pointer'
                                      : ''
                                  }`}
                                  onClick={() => {
                                    navigateVideoPage();
                                  }}
                                >
                                  {selectedVideoName}
                                </span>
                                <div
                                  className="items-center justify-center cursor-pointer ml-2 mr-7 hidden group-hover:flex w-4"
                                  onClick={() => handleIsEditible(true)}
                                >
                                  <EditwriteIcon className="w-4 h-4" />
                                </div>
                              </div>
                            </>
                          )}

                          <div className="flex">
                            <p className="text-[#111] text-[13px] flex items-center">
                              Date added:{' '}
                              {dayjs.utc(new Date(selectedVideo.date_added)).format('MMMM DD, YYYY hh:mm A')}
                            </p>
                          </div>

                          <div className="flex">
                            {selectedVideo && (
                              <p className="text-[#111] text-[13px] flex items-center">
                                Meeting time:{' '}
                                {dayjs.utc(new Date(selectedVideo.meeting_time)).format('MMMM DD, YYYY hh:mm A')}
                              </p>
                            )}

                            <ChevronDownIcon
                              className={`ml-auto cursor-pointer w-6 h-6 transform duration-100 inline-block ${
                                isExpanded ? '-rotate-180' : 'rotate-0'
                              }`}
                              onClick={() => setExpanded(!isExpanded)}
                            />
                          </div>
                          <>
                            {selectedChannel && channelId !== 'home' ? (
                              <>
                                {selectedVideo && selectedVideo?.playlists?.length !== 0 ? (
                                  <p className={`text-[13px] mt-1 ${isExpanded ? '' : 'line-clamp-1'}`}>
                                    Collections:{' '}
                                    {[...(selectedVideo?.playlists || [])]
                                      ?.sort((a: any, b: any) => {
                                        if (a.name.toLowerCase() < b.name.toLowerCase()) {
                                          return -1;
                                        }
                                        if (a.name.toLowerCase() > b.name.toLowerCase()) {
                                          return 1;
                                        }
                                        return 0;
                                      })
                                      .map((playlist: any, idx: number) => (
                                        <span
                                          className="text-[#1664A3] font-bold hover:underline cursor-pointer"
                                          key={playlist.id}
                                          onClick={() => navigatePlaylist(playlist)}
                                        >
                                          {idx !== 0 && ', '}
                                          {playlist.name}
                                        </span>
                                      ))}
                                  </p>
                                ) : (
                                  <p className="text-[13px] mt-1 truncate">Not shared in any collections</p>
                                )}
                              </>
                            ) : (
                              <>
                                {selectedVideo?.channels && selectedVideo.channels.length !== 0 ? (
                                  <p className={`text-[13px] text-[#111] mt-1 ${isExpanded ? '' : 'line-clamp-1'}`}>
                                    Shared in{' '}
                                    {[...selectedVideo.channels]
                                      .sort((channelA: { is_archived: any }, ChannelB: { is_archived: any }) => {
                                        if (channelA.is_archived === ChannelB.is_archived) {
                                          return 0;
                                        }
                                        if (channelA.is_archived) {
                                          return 1;
                                        } else {
                                          return -1;
                                        }
                                      })
                                      .map((item: any, idx: number) => (
                                        <span
                                          className={` ${
                                            item.is_archived
                                              ? ''
                                              : 'text-[#1664A3] font-bold hover:underline cursor-pointer '
                                          }`}
                                          key={item.channel.id}
                                          onClick={() => {
                                            if (item.is_archived) {
                                              return;
                                            }
                                            setSelectedChannel(
                                              channelList.find((channel) => channel.hash_id === item.channel.hash_id)
                                            );
                                            navigateChannel(item.channel.hash_id);
                                          }}
                                        >
                                          {idx !== 0 && ', '}
                                          {`#${item.channel.name}${item.is_archived ? ' (archived)' : ''}`}
                                        </span>
                                      ))}
                                  </p>
                                ) : (
                                  <p className="text-[13px] mt-1 truncate">Not shared in any channels</p>
                                )}
                              </>
                            )}
                          </>
                          <>
                            {videoShotsData && videoShotsData?.length > 1 && (
                              <VideoShotDropdown channelId={channelId} videoShotsData={videoShotsData} />
                            )}
                          </>
                        </div>
                        {isExpanded && (
                          <>
                            <div className="px-4">
                              {selectedVideo.hash_id && channelId && (
                                <VideoStatistics
                                  channelId={channelId}
                                  videoHashId={selectedVideo.hash_id}
                                  channelName={selectedChannel?.name}
                                />
                              )}
                            </div>
                            <div className="px-4">
                              <div className="text-[13px] text-[#111] pb-4">
                                <div className="channel-info channel-info-desktop"></div>
                                <p className="pt-4">Owner</p>
                                <span className="font-normal text-[#1D1C1D]">
                                  {selectedVideo?.owner?.display_name
                                    ? selectedVideo.owner?.display_name
                                    : selectedVideo?.owner?.name}
                                </span>
                                {selectedVideo && videoShotsData && (
                                  <PeopleList
                                    shots={videoShotsData}
                                    currentShot={currentVideoShot}
                                    videoHashId={selectedVideo.hash_id}
                                  ></PeopleList>
                                )}

                                <p className="pt-4">Source</p>
                                <span className="font-normal text-[#1D1C1D]">{selectedVideo?.video_source}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  {selectedVideo === null && !videoId && <VideoInfoSkeleton />}
                </div>
                <div className="flex px-4 mt-2">
                  <AntTabs
                    ScrollButtonComponent={tabScrollButtonRef}
                    value={selected}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    className="font-lato"
                    aria-label="scrollable auto tabs example"
                  >
                    <AntTab label="Overview" />
                    <AntTab label="Chapters" />
                    <AntTab label="Timeline" />
                    <AntTab label="Notes" />
                    <AntTab label="Comments" />
                    <AntTab label="Transcript" />
                    {/* <AntTab label="Viewers" /> */}
                    <AntTab label="Search" sx={{ visibility: 'hidden', width: '0px', margin: 0 }} />
                  </AntTabs>
                  <div
                    className={clsx(
                      'flex items-center justify-center px-3 bg-[rgba(0,0,0,0.05)]h-8 cursor-pointer rounded-lg ml-2',
                      {
                        'bg-black': selected === 6,
                        'text-white': selected === 6,
                        'hover:bg-[rgba(0,0,0,0.1)]': selected !== 6
                      }
                    )}
                    onClick={() => {
                      setSelected(6);
                      dispatch(
                        setSelectedTab({ channelHashId: channelId, videoHashId: secondaryViewData.hashId, tab: 6 })
                      );
                    }}
                  >
                    <SearchIcon className="w-[20px] h-[20px]" />
                  </div>
                </div>
                <div className={`py-2 px-4 text-[13px] text-[#111] leading-6 ${selected === 4 ? '' : 'hidden'}`}>
                  <input
                    type="checkbox"
                    id="highlight-keyword"
                    name="highlight-keyword"
                    checked={showHighlight}
                    className="focus:ring-offset-0 ring-offset-0 rounded-[4px] cursor-pointer"
                    onChange={(event: any) => handleShowHighlight(event.target.checked)}
                  />
                  <label htmlFor="highlight-keyword" className="text-[13px] text-[#111] mb-2 ml-[5px] cursor-pointer">
                    Highlight keywords
                  </label>
                </div>
                <div className={`relative grow overflow-hidden ${!videoId && 'border-t border-line'} mt-4`}>
                  {!loading && selectedVideo && selectedChannel && videoShotsData && (
                    <>
                      {selected == 0 && (
                        <OverView
                          key={selectedChannel.id + selectedVideo.id}
                          secondaryViewData={secondaryViewData}
                          currentVideoShot={currentVideoShot}
                          secondaryViewSlice={secondaryViewSlice}
                          selected={selected}
                          videoShots={videoShotsData}
                        />
                      )}
                      {selected == 1 && (
                        <ChapterView
                          key={selectedChannel.id + selectedVideo.id}
                          navigateVideoPage={navigateVideoPage}
                          currentVideoShot={currentVideoShot}
                          videoHashId={secondaryViewData.hashId}
                          channelId={secondaryViewData.channelId}
                          selected={selected}
                          videoShots={videoShotsData}
                          secondaryViewSlice={secondaryViewSlice}
                          aesKeyId={selectedVideo.keyframe_aes_key_id}
                          decryptionKeys={encryptionData}
                        />
                      )}
                      {selected == 2 && (
                        <TimelineView
                          key={selectedChannel.id + selectedVideo.id}
                          navigateVideoPage={navigateVideoPage}
                          currentVideoShot={currentVideoShot}
                          videoHashId={secondaryViewData.hashId}
                          channelId={secondaryViewData.channelId}
                          selected={selected}
                          videoShots={videoShotsData}
                          aesKeyId={selectedVideo.keyframe_aes_key_id}
                          decryptionKeys={encryptionData}
                          secondaryViewSlice={secondaryViewSlice}
                          isResizing={isResizing}
                        />
                      )}
                      {selected === 3 && (
                        <NoteView
                          key={channelId + secondaryViewData.hashId}
                          channelId={secondaryViewData.channelId}
                          navigateVideoPage={navigateVideoPage}
                          jumpToVideo={jumpToVideo}
                          currentVideoShot={currentVideoShot}
                          selectedVideo={selectedVideo}
                          setCurrentVideoShot={setCurrentVideoShot}
                          videoId={secondaryViewData.id}
                          videoHashId={secondaryViewData.hash_id}
                          secondaryViewData={secondaryViewData}
                          selected={selected}
                          videoShots={videoShotsData}
                        />
                      )}
                      {selected === 4 && (
                        <ChatView
                          key={channelId + secondaryViewData.hashId}
                          channelId={secondaryViewData.channelId}
                          navigateVideoPage={navigateVideoPage}
                          videoId={secondaryViewData.id}
                          videoHashId={secondaryViewData.hashId}
                          selectedVideo={selectedVideo}
                          selectedChannel={selectedChannel}
                          setSelectedChannel={setSelectedChannel}
                          navigateChannel={navigateChannel}
                        />
                      )}
                      {selected === 5 && (
                        <TranscriptView2
                          key={channelId + secondaryViewData.hashId}
                          secondaryViewData={secondaryViewData}
                          navigateVideoPage={navigateVideoPage}
                          keywords={keywords}
                          showHighlight={showHighlight}
                          currentVideoShot={currentVideoShot}
                          videoHashId={secondaryViewData.hashId}
                          channelId={secondaryViewData.channelId}
                          secondaryViewSlice={secondaryViewSlice}
                          selected={selected}
                          videoShots={videoShotsData}
                          isResizing={isResizing}
                        />
                      )}
                      {selected === 6 && (
                        <SearchView
                          key={channelId + secondaryViewData.hashId}
                          videoHashId={secondaryViewData.hashId}
                          videoShots={videoShotsData}
                          currentVideoShot={currentVideoShot}
                          selected={selected}
                          channelId={secondaryViewData.channelId}
                          aesKeyId={selectedVideo?.keyframe_aes_key_id}
                          decryptionKeys={encryptionData}
                          navigateVideoPage={navigateVideoPage}
                          isResizing={isResizing}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default memo(SecondaryView);
